import { useQuery } from "@apollo/client";
import { useState } from "react";
import {
  FaChartBar,
  FaChartLine,
  FaCoins,
  FaCrown,
  FaTrophy,
  FaUsers,
} from "react-icons/fa";
import { GET_TIP_STATISTICS } from "../../../queries/broadcasterQueries";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "../../charts/RechartsWrapper";
import LoadingPage from "../../LoadingPage";

type TipStatisticsData = {
  totalTips: number;
  totalTokens: number;
  averageTip: number;
  medianTip: number;
  biggestTip: number;
  smallestTip: number;
  uniqueTippers: number;
  mostCommonTipAmount: { amount: number; count: number } | null;
  tipsByDay: Array<{ date: string; count: number; tokens: number }>;
  tipsByHour: Array<{ hour: number; count: number; tokens: number }>;
  topTipAmounts: Array<{ amount: number; count: number }>;
};

const COLORS = [
  "#8b5cf6",
  "#ec4899",
  "#f59e0b",
  "#10b981",
  "#3b82f6",
  "#ef4444",
  "#14b8a6",
  "#f97316",
  "#a855f7",
  "#06b6d4",
];

const TipStatistics = () => {
  const [selectedRange, setSelectedRange] = useState("today");

  const { loading, error, data } = useQuery(GET_TIP_STATISTICS, {
    variables: { range: selectedRange },
  });

  if (loading) return <LoadingPage />;
  if (error) return <div className="alert alert-error">{error.message}</div>;

  const stats: TipStatisticsData = data?.getTipStatistics;

  if (!stats) {
    return <div className="alert alert-warning">No statistics available</div>;
  }

  const rangeOptions = [
    { value: "today", label: "Today" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "year", label: "This Year" },
    { value: "alltime", label: "All Time" },
  ];

  // Format hour data for better display - convert UTC to local time
  const hourData = stats.tipsByHour
    .map((h) => {
      // Create a date at the UTC hour and convert to local
      const utcDate = new Date();
      utcDate.setUTCHours(h.hour, 0, 0, 0);
      const localHour = utcDate.getHours();
      const displayHour = localHour % 12 || 12;
      const period = localHour >= 12 ? "PM" : "AM";
      return {
        count: h.count,
        tokens: h.tokens,
        hour: localHour,
        hourLabel: `${displayHour}:00 ${period}`,
      };
    })
    .sort((a, b) => a.hour - b.hour);

  // Format day data for better display
  const dayData = stats.tipsByDay.map((d) => ({
    ...d,
    dateLabel: new Date(d.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
  }));

  return (
    <div className="min-h-screen bg-base-200 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <FaChartBar className="text-primary text-3xl" />
            <h1 className="text-3xl font-bold text-base-content">
              Tip Statistics
            </h1>
          </div>

          {/* Range Selector */}
          <div className="flex gap-2 flex-wrap">
            {rangeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedRange(option.value)}
                className={`btn btn-sm ${
                  selectedRange === option.value
                    ? "btn-primary"
                    : "btn-ghost btn-outline"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Tips */}
          <div className="stats shadow bg-base-100">
            <div className="stat">
              <div className="stat-figure text-primary">
                <FaCoins className="text-3xl" />
              </div>
              <div className="stat-title">Total Tips</div>
              <div className="stat-value text-primary">
                {stats.totalTips.toLocaleString()}
              </div>
              <div className="stat-desc">
                {stats.totalTokens.toLocaleString()} tokens
              </div>
            </div>
          </div>

          {/* Average Tip */}
          <div className="stats shadow bg-base-100">
            <div className="stat">
              <div className="stat-figure text-secondary">
                <FaChartLine className="text-3xl" />
              </div>
              <div className="stat-title">Average Tip</div>
              <div className="stat-value text-secondary">
                {stats.averageTip.toFixed(1)}
              </div>
              <div className="stat-desc">Median: {stats.medianTip} tokens</div>
            </div>
          </div>

          {/* Biggest Tip */}
          <div className="stats shadow bg-base-100">
            <div className="stat">
              <div className="stat-figure text-warning">
                <FaCrown className="text-3xl" />
              </div>
              <div className="stat-title">Biggest Tip</div>
              <div className="stat-value text-warning">{stats.biggestTip}</div>
              <div className="stat-desc">
                Smallest: {stats.smallestTip} tokens
              </div>
            </div>
          </div>

          {/* Unique Tippers */}
          <div className="stats shadow bg-base-100">
            <div className="stat">
              <div className="stat-figure text-success">
                <FaUsers className="text-3xl" />
              </div>
              <div className="stat-title">Unique Tippers</div>
              <div className="stat-value text-success">
                {stats.uniqueTippers}
              </div>
              <div className="stat-desc">
                {stats.mostCommonTipAmount
                  ? `Most common: ${stats.mostCommonTipAmount.amount} tokens`
                  : "N/A"}
              </div>
            </div>
          </div>
        </div>

        {/* Most Common Tip */}
        {stats.mostCommonTipAmount && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex items-center gap-3">
                <FaTrophy className="text-warning text-2xl" />
                <h2 className="card-title">Most Popular Tip Amount</h2>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-5xl font-bold text-primary">
                  {stats.mostCommonTipAmount.amount}
                </div>
                <div>
                  <div className="text-base-content/70">tokens</div>
                  <div className="text-sm text-base-content/50">
                    Tipped {stats.mostCommonTipAmount.count} times
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tips by Day */}
          {dayData.length > 0 && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Tips Over Time</h2>
                <ResponsiveContainer width="100%" height={300} as any>
                  <LineChart data={dayData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dateLabel" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="count"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      name="Number of Tips"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="tokens"
                      stroke="#10b981"
                      strokeWidth={2}
                      name="Total Tokens"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Tips by Hour */}
          {hourData.length > 0 && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Tips by Hour of Day</h2>
                <ResponsiveContainer width="100%" height={300} as any>
                  <BarChart data={hourData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hourLabel" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#8b5cf6" name="Number of Tips" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Biggest Revenue Generators */}
          {stats.topTipAmounts.length > 0 && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Biggest Revenue Generators</h2>
                <p className="text-sm text-base-content/60 mb-2">
                  Total tokens earned by each tip amount
                </p>
                <ResponsiveContainer width="100%" height={300} as any>
                  <BarChart
                    data={stats.topTipAmounts.map((t) => ({
                      ...t,
                      totalTokens: t.amount * t.count,
                      label: `${t.amount}tk`,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="totalTokens"
                      fill="#10b981"
                      name="Total Tokens"
                    >
                      {stats.topTipAmounts.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top Tip Amounts */}
          {stats.topTipAmounts.length > 0 && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Top Tip Amounts</h2>
                <ResponsiveContainer width="100%" height={300} as any>
                  <BarChart data={stats.topTipAmounts}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="amount" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#ec4899" name="Times Tipped">
                      {stats.topTipAmounts.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Tip Distribution Pie Chart */}
          {stats.topTipAmounts.length > 0 && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Tip Frequency Distribution</h2>
                <p className="text-sm text-base-content/60 mb-2">
                  By number of times tipped
                </p>
                <ResponsiveContainer width="100%" height={300} as any>
                  <PieChart>
                    <Pie
                      data={stats.topTipAmounts.slice(0, 5)}
                      dataKey="count"
                      nameKey="amount"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={(entry: { amount: number; count: number }) =>
                        `${entry.amount} tokens`
                      }
                    >
                      {stats.topTipAmounts.slice(0, 5).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Revenue Distribution Pie Chart */}
          {stats.topTipAmounts.length > 0 && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Revenue Distribution</h2>
                <p className="text-sm text-base-content/60 mb-2">
                  By total tokens earned
                </p>
                <ResponsiveContainer width="100%" height={300} as any>
                  <PieChart>
                    <Pie
                      data={stats.topTipAmounts
                        .map((t) => ({
                          ...t,
                          totalTokens: t.amount * t.count,
                        }))
                        .sort((a, b) => b.totalTokens - a.totalTokens)
                        .slice(0, 5)}
                      dataKey="totalTokens"
                      nameKey="amount"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={(entry: { amount: number; totalTokens: number }) =>
                        `${
                          entry.amount
                        }tk: ${entry.totalTokens.toLocaleString()}`
                      }
                    >
                      {stats.topTipAmounts.slice(0, 5).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Detailed Stats Table */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Detailed Breakdown</h2>
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Total Tips Received</td>
                    <td className="font-bold">
                      {stats.totalTips.toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td>Total Tokens Received</td>
                    <td className="font-bold">
                      {stats.totalTokens.toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td>Average Tip Amount</td>
                    <td className="font-bold">{stats.averageTip.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Median Tip Amount</td>
                    <td className="font-bold">{stats.medianTip}</td>
                  </tr>
                  <tr>
                    <td>Largest Single Tip</td>
                    <td className="font-bold">{stats.biggestTip}</td>
                  </tr>
                  <tr>
                    <td>Smallest Single Tip</td>
                    <td className="font-bold">{stats.smallestTip}</td>
                  </tr>
                  <tr>
                    <td>Number of Unique Tippers</td>
                    <td className="font-bold">{stats.uniqueTippers}</td>
                  </tr>
                  <tr>
                    <td>Tips per Tipper (Average)</td>
                    <td className="font-bold">
                      {(stats.totalTips / stats.uniqueTippers).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TipStatistics;
